import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookmarkPlus, CalendarClock, Download, Loader2, Mail } from "lucide-react";
import { api } from "../lib/api";
import { PageShell } from "../components/PageShell";
import { formatApiError, useAuth } from "../context/AuthContext";

const TONES = ["warm", "hopeful", "honest", "playful", "wise", "fierce"];

export default function Letters() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selves, setSelves] = useState([]);
  const [letters, setLetters] = useState([]);
  const [form, setForm] = useState({ years: 10, self_id: "", goal: "", tone: "warm" });
  const [current, setCurrent] = useState(null);
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/journey/${id}/letters`).then(({ data }) => setLetters(data));
  useEffect(() => { api.get(`/journey/${id}/selves`).then(({ data }) => setSelves(data)); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); } };
  const write = () => run(async () => { const { data } = await api.post(`/journey/${id}/letters`, { ...form, self_id: form.self_id || null }); setCurrent(data); load(); });
  const toJournal = () => run(async () => { await api.post(`/letters/${current.id}/journal`); toast.success("Letter added to your journal."); });
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([current.body + "\n\n— A fictional letter from Rabbit Hole Futures"], { type: "text/plain" }));
    a.download = "letter-from-future-me.txt";
    a.click();
  };
  const schedule = () => {
    if (!user) return navigate(`/login?next=/letters/${id}`);
    if (!when) return toast.error("Pick a delivery date");
    run(async () => { await api.post(`/letters/${current.id}/schedule`, { deliver_at: new Date(when).toISOString() }); toast.success(`It will arrive at ${user.email}.`); load(); });
  };
  const sel = "bg-[#0A051A] border border-white/15 rounded-xl px-3 py-2 text-sm text-white w-full";

  return (
    <PageShell back={`/journey/${id}`} label="Letter from the future" title={<>A letter from <span className="text-glow-gold">future you</span></>}
      subtitle="Choose who is writing and why. You'll get a fictional letter, written as a story, not a prediction.">
      <div className="glass rounded-3xl p-6 grid md:grid-cols-2 gap-4" data-testid="letter-form">
        <label><span className="font-mono-label block mb-1">Future age</span>
          <select className={sel} value={form.years} onChange={(e) => setForm({ ...form, years: Number(e.target.value) })} data-testid="letter-years">
            {[5, 10, 20, 30].map((y) => <option key={y} value={y}>{y} years from now</option>)}</select></label>
        <label><span className="font-mono-label block mb-1">Timeline</span>
          <select className={sel} value={form.self_id} onChange={(e) => setForm({ ...form, self_id: e.target.value })} data-testid="letter-timeline">
            <option value="">My own future (no specific timeline)</option>{selves.map((s) => <option key={s.id} value={s.id}>{s.timeline_name}</option>)}</select></label>
        <label><span className="font-mono-label block mb-1">Personal goal</span>
          <input className={sel} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="Open my own studio" data-testid="letter-goal" /></label>
        <label><span className="font-mono-label block mb-1">Emotional tone</span>
          <select className={sel} value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })} data-testid="letter-tone">{TONES.map((t) => <option key={t}>{t}</option>)}</select></label>
        <button onClick={write} disabled={busy} className="btn-pill btn-pill-gold md:col-span-2 justify-center" data-testid="letter-write">{busy && !current ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Receive my letter</button>
      </div>

      {current && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-3xl p-8 border border-amber-300/30 bg-[#0d0920]" data-testid="letter-body">
          <div className="font-mono-label text-amber-200/80 mb-4">From {current.years} years ahead{current.timeline ? ` · ${current.timeline}` : ""}</div>
          <div className="whitespace-pre-line font-display font-light text-lg leading-relaxed text-white/90">{current.body}</div>
          <div className="flex flex-wrap gap-2 mt-8 items-center">
            <button onClick={toJournal} disabled={busy} className="btn-pill text-xs" data-testid="letter-journal"><BookmarkPlus size={12} /> Add to journal</button>
            <button onClick={download} className="btn-pill text-xs" data-testid="letter-download"><Download size={12} /> Download</button>
            <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="bg-[#0A051A] border border-white/15 rounded-full px-3 py-1.5 text-xs text-white" data-testid="letter-when" />
            <button onClick={schedule} disabled={busy} className="btn-pill btn-pill-gold text-xs" data-testid="letter-schedule"><CalendarClock size={12} /> Schedule email delivery</button>
          </div>
          {!user && <p className="text-[11px] text-white/40 mt-2">Sign in to have this letter emailed to you later.</p>}
        </motion.div>
      )}

      {letters.length > 0 && <div className="mt-10 space-y-2" data-testid="letter-list"><div className="font-mono-label mb-2">Your letters</div>
        {letters.map((l) => <button key={l.id} onClick={() => setCurrent(l)} className="w-full text-left glass rounded-2xl px-5 py-3 text-sm flex justify-between gap-3">
          <span>{l.years} years ahead · {l.tone}{l.goal ? ` · ${l.goal}` : ""}</span>
          <span className="text-xs text-white/45">{l.status === "scheduled" ? `Arrives ${new Date(l.deliver_at).toLocaleString()}` : l.status}</span></button>)}</div>}
    </PageShell>
  );
}
